/**
 * Simple Image Compression Utility
 * Compresses images larger than 500KB
 */

export interface CompressionOptions {
  maxSizeBytes?: number;
  targetSizeBytes?: number;
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  mimeType?: string;
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  wasCompressed: boolean;
}

/**
 * Compresses an image file if it exceeds the threshold
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> => {
  const {
    maxSizeBytes = 500 * 1024,
    targetSizeBytes = 400 * 1024,
    quality = 0.8,
    maxWidth = 1920,
    maxHeight = 1920,
    mimeType = 'image/jpeg',
  } = options;

  const originalSize = file.size;

  // If file is smaller than threshold, return as-is
  if (originalSize <= maxSizeBytes) {
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 1.0,
      wasCompressed: false,
    };
  }

  try {
    const canvas = await loadImageToCanvas(file, maxWidth, maxHeight);
    const compressedBlob = await canvasToBlob(canvas, mimeType, quality);
    
    const compressedFile = new File(
      [compressedBlob],
      file.name.replace(/\.[^/.]+$/, '.jpg'),
      { type: mimeType }
    );

    const compressedSize = compressedFile.size;

    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      compressionRatio: compressedSize / originalSize,
      wasCompressed: true,
    };
  } catch (error) {
    console.error('Image compression failed:', error);
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 1.0,
      wasCompressed: false,
    };
  }
};

const loadImageToCanvas = (
  file: File,
  maxWidth: number,
  maxHeight: number
): Promise<HTMLCanvasElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      mimeType,
      quality
    );
  });
};

/**
 * Compress multiple images in parallel
 */
export const compressImages = async (
  files: File[],
  options: CompressionOptions = {}
): Promise<CompressionResult[]> => {
  return Promise.all(files.map((file) => compressImage(file, options)));
};

